<template>
  <div class="tglPill tglPill--mini" :class="{ 'tglPill--active': open }" title="Laufende und aktuelle Upscale-Jobs" @click="open = true">
    <v-icon x-small>mdi-image-auto-adjust</v-icon> Upscales
    <span v-if="runningCount" class="upBtn__count upBtn__count--running">
      <v-progress-circular indeterminate size="9" width="2" class="mr-1" />{{ runningCount }}
    </span>
    <span v-else-if="jobs.length" class="upBtn__count">{{ jobs.length }}</span>

    <v-dialog v-model="open" max-width="1500" scrollable>
      <v-card class="upDialog">
        <div class="upDialog__head">
          <div class="upDialog__title">
            <v-icon small class="mr-2">mdi-image-auto-adjust</v-icon>Upscales
            <span class="upDialog__sub">letzte 24 Stunden · {{ runningCount }} laufend</span>
            <!-- Kosten der angezeigten Jobs, aufgeschlüsselt nach Schritt -->
            <span v-if="costs.total > 0" class="upDialog__costs" :title="`Kosten der ${visibleJobs.length} angezeigten Jobs (Modellkosten, ohne Function-Laufzeit)`">
              <b>≈ {{ formatUsd(costs.total) }}</b>
              <span v-for="part in costs.parts" :key="part.key" class="upDialog__cost">{{ part.label }} {{ formatUsd(part.value) }}</span>
            </span>
            <!-- mittlere Farbverschiebung der Upscales (Rotstich-Beobachtung) -->
            <span v-if="colorShiftAvg" class="upDialog__costs" :title="`Mittlere Farbverschiebung Artwork → Upscale über ${colorShiftAvg.n} Jobs (Mittelwert je Kanal, 0–255)`">
              <span class="upDialog__cost">Ø Farbe {{ colorShiftAvg.label }}</span>
            </span>
          </div>
          <div class="upDialog__filters">
            <span
              v-for="f in filters"
              :key="f.value"
              class="upDialog__filter"
              :class="{ 'upDialog__filter--active': sourceFilter === f.value }"
              @click.stop="sourceFilter = f.value"
            >{{ f.label }} <b>{{ f.count }}</b></span>
            <v-btn icon small @click.stop="open = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </div>
        <v-card-text class="upDialog__body">
          <div v-if="error" class="upDialog__error">{{ error }}</div>
          <div v-else-if="!visibleJobs.length" class="upDialog__empty">Keine Upscale-Jobs in den letzten 24 Stunden.</div>
          <template v-else>
            <UpscaleJobCard v-if="openJob" :key="openJob.id" :job="openJob" class="mb-4" @close="openJobId = null" />
            <div class="upDialog__grid">
              <UpscaleJobTile
                v-for="job in visibleJobs"
                :key="job.id"
                :job="job"
                :active="job.id === openJobId"
                @open="openJobId = openJobId === job.id ? null : job.id"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import UpscaleJobCard from "@/components/Upscales/UpscaleJobCard.vue";
import UpscaleJobTile from "@/components/Upscales/UpscaleJobTile.vue";
import { isJobRunning, subscribeRecentUpscaleJobs, formatUsd, formatColorShift } from "@/upscaleJobs";

/**
 * Button im Funktions-Header der Import-Ansicht: zeigt, wie viele Upscale-Jobs laufen,
 * und öffnet die Liste (Kacheln → große Karte). Laufende Jobs stehen oben.
 */
export default {
  name: "UpscalesButton",
  components: { UpscaleJobCard, UpscaleJobTile },
  data() {
    return { open: false, jobs: [], error: "", sourceFilter: "order", openJobId: null, unsubscribe: null };
  },
  computed: {
    runningCount() {
      return this.jobs.filter(isJobRunning).length;
    },
    filters() {
      const count = (source) => this.jobs.filter((j) => !source || j.source === source).length;
      return [
        { value: "order", label: "Bestellungen", count: count("order") },
        { value: "lab", label: "Lab", count: count("lab") },
        { value: "test", label: "Test", count: count("test") },
        { value: "", label: "Alle", count: count("") },
      ];
    },
    visibleJobs() {
      const list = this.jobs.filter((j) => !this.sourceFilter || j.source === this.sourceFilter);
      // laufende zuerst, sonst neueste zuerst (so kommen sie schon aus der Abfrage)
      return [...list.filter(isJobRunning), ...list.filter((j) => !isJobRunning(j))];
    },
    openJob() {
      return this.jobs.find((j) => j.id === this.openJobId) || null;
    },
    /** Mittlere Farbverschiebung der sichtbaren Jobs mit Messwert */
    colorShiftAvg() {
      const shifts = this.visibleJobs.map((j) => j.stages && j.stages.upscale && j.stages.upscale.colorShift).filter(Boolean);
      if (!shifts.length) return null;
      const avg = (k) => shifts.reduce((a, s) => a + (Number(s[k]) || 0), 0) / shifts.length;
      return { n: shifts.length, label: formatColorShift({ r: avg("r"), g: avg("g"), b: avg("b") }) };
    },
    /** Summen der sichtbaren Jobs: Upscale (stages.upscale), Score (report.analysis), Detail-Pass (report.detail), Freistellen (Twin) */
    costs() {
      const sum = (pick) => this.visibleJobs.reduce((acc, j) => acc + (Number(pick(j)) || 0), 0);
      const parts = [
        { key: "upscale", label: "Upscale", value: sum((j) => j.stages && j.stages.upscale && j.stages.upscale.costUsd) },
        { key: "score", label: "Score", value: sum((j) => j.report && j.report.analysis && j.report.analysis.costUsd) },
        { key: "detail", label: "Detail-Pass", value: sum((j) => j.report && j.report.detail && j.report.detail.costUsd) },
        { key: "cutout", label: "Freistellen", value: sum((j) => j.report && j.report.cutout && j.report.cutout.twin && j.report.cutout.twin.costUsd) },
      ].filter((p) => p.value > 0);
      // Gesamt = die Summe je Job (enthält auch Anteile, die keiner Kategorie zugeordnet sind)
      return { total: sum((j) => j.costUsd), parts };
    },
  },
  methods: { formatUsd },
  mounted() {
    this.unsubscribe = subscribeRecentUpscaleJobs(
      { hours: 24 },
      (jobs) => {
        this.jobs = jobs;
        this.error = "";
      },
      (err) => {
        this.error = err.code === "permission-denied" ? "Kein Zugriff auf upscaleJobs — die Firestore-Regel für diese Collection fehlt noch." : err.message;
      }
    );
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
};
</script>

<style lang="scss" scoped>
.upBtn__count {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  background: #edeafa;
  color: #6a4fc4;
  border-radius: 999px;
  padding: 1px 8px;

  &--running {
    background: #7b5cd6;
    color: #fff;
  }
}

.upDialog {
  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    flex-wrap: wrap;
  }
  &__title {
    display: flex;
    align-items: center;
    font-size: 17px;
    font-weight: 700;
  }
  &__sub {
    margin-left: 10px;
    font-size: 12.5px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.5);
  }
  &__costs {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-left: 14px;
    font-size: 12px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.6);
    white-space: nowrap;
  }
  &__cost {
    padding: 1px 8px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.05);
    font-size: 11.5px;
  }
  &__filters {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  &__filter {
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;

    b {
      margin-left: 4px;
      font-size: 11px;
      color: #6a4fc4;
    }
    &:hover {
      background: #f4f3f7;
    }
    &--active {
      background: rgba(123, 92, 214, 0.12);
      color: #7b5cd6;
    }
  }
  &__body {
    padding: 18px 20px !important;
    background: #faf9fc;
    min-height: 320px;
  }
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 12px;
  }
  &__empty,
  &__error {
    padding: 80px 0;
    text-align: center;
    color: rgba(0, 0, 0, 0.5);
  }
  &__error {
    color: #a3342c;
  }
}
</style>
